// Base skeleton component
const SkeletonBase = ({ className = "", animate = true }) => (
  <div
    className={`bg-slate-700/50 rounded-lg ${
      animate ? "animate-pulse" : ""
    } ${className}`}
  />
);

// Input skeleton
export const SkeletonInput = ({ height = "h-12", className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <SkeletonBase className="h-4 w-24" />
    <SkeletonBase className={`w-full ${height}`} />
  </div>
);

// Textarea skeleton
export const SkeletonTextarea = ({ rows = 4, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <SkeletonBase className="h-4 w-32" />
    <SkeletonBase className={`w-full h-${rows * 6}`} />
  </div>
);

// Rich text editor skeleton
export const SkeletonRichTextEditor = ({ className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <SkeletonBase className="h-4 w-20" />
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/50 p-4">
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex gap-2 border-b border-slate-600/50 pb-3">
          {[
            "toolbar-1",
            "toolbar-2",
            "toolbar-3",
            "toolbar-4",
            "toolbar-5",
            "toolbar-6",
            "toolbar-7",
            "toolbar-8",
          ].map((id) => (
            <SkeletonBase key={id} className="w-8 h-8" />
          ))}
        </div>
        {/* Content area */}
        <div className="space-y-3">
          {[
            "content-1",
            "content-2",
            "content-3",
            "content-4",
            "content-5",
            "content-6",
            "content-7",
            "content-8",
            "content-9",
            "content-10",
            "content-11",
            "content-12",
          ].map((id, i) => (
            <SkeletonBase
              key={id}
              className={`h-4 ${
                i % 4 === 3 ? "w-3/4" : i % 3 === 1 ? "w-5/6" : "w-full"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Section skeleton
export const SkeletonSection = ({
  title,
  icon: Icon,
  children,
  className = "",
}) => (
  <div
    className={`bg-slate-800/95 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-lg ${className}`}
  >
    <div className="px-4 py-3 border-b border-slate-600/50">
      <div className="flex items-center space-x-3">
        {Icon && <Icon className="w-5 h-5 text-blue-400" />}
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// FAQ skeleton
export const SkeletonFAQ = ({ count = 2, className = "" }) => (
  <div className={`space-y-4 ${className}`}>
    {Array.from({ length: count }, (_, i) => (
      <div
        key={`faq-item-${i + 1}`}
        className="bg-slate-800/30 p-6 rounded-xl border border-slate-600/50 shadow-lg"
      >
        <div className="space-y-3">
          <SkeletonBase className="h-6 w-3/4" />
          <div className="space-y-2">
            <SkeletonBase className="h-4 w-full" />
            <SkeletonBase className="h-4 w-5/6" />
            <SkeletonBase className="h-4 w-4/5" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Tag skeleton
export const SkeletonTags = ({ count = 3, className = "" }) => (
  <div className={`flex flex-wrap gap-1.5 ${className}`}>
    {Array.from({ length: count }, (_, i) => (
      <SkeletonBase
        key={`tag-${i + 1}`}
        className={`h-7 ${
          i === 0 ? "w-16" : i === 1 ? "w-20" : "w-12"
        } rounded-md`}
      />
    ))}
  </div>
);

// Image skeleton
export const SkeletonImage = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <SkeletonBase className="w-full h-64 rounded-lg" />
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-12 h-12 border-2 border-slate-500/50 rounded-lg flex items-center justify-center">
        <svg
          className="w-6 h-6 text-slate-500/50"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  </div>
);

// Radio group skeleton
export const SkeletonRadioGroup = ({ options = 2, className = "" }) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: options }, (_, i) => (
      <div key={`radio-${i + 1}`} className="flex items-center space-x-2">
        <SkeletonBase className="w-4 h-4 rounded-full" />
        <SkeletonBase className="h-4 w-20" />
      </div>
    ))}
  </div>
);

// Select skeleton
export const SkeletonSelect = ({ className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <SkeletonBase className="h-4 w-16" />
    <SkeletonBase className="w-full h-10" />
  </div>
);

// Header skeleton
export const HeaderSkeleton = () => (
  <header className="fixed top-0 w-full backdrop-blur-md z-50 transition-all duration-300 border-b bg-black/95 border-gray-800/50">
    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-slate-400/60 to-transparent animate-pulse"></div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-[76px]">
        <div className="flex items-center space-x-4">
          <SkeletonBase className="w-10 h-10 rounded-lg" />
          <div className="space-y-1">
            <SkeletonBase className="h-5 w-32" />
            <SkeletonBase className="h-3 w-24" />
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <SkeletonBase className="h-12 w-20 rounded" />
          <SkeletonBase className="h-12 w-24 rounded" />
        </div>
      </div>
    </div>
  </header>
);

// Complete loading skeleton for edit page
export const EditPageSkeleton = () => (
  <div className="min-h-screen bg-black relative">
    <HeaderSkeleton />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-8 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content Area - Primary Column (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Content Section */}
          <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-lg">
            <div className="px-4 py-3 border-b border-slate-600/50">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-slate-700/50 rounded animate-pulse"></div>
                <h3 className="text-sm font-semibold text-white">Content</h3>
              </div>
            </div>
            <div className="p-4 space-y-6">
              {/* Title Input */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-12" />
                <SkeletonBase className="w-full h-14 rounded-md" />
              </div>
              {/* Rich Text Editor */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-16" />
                <div className="bg-slate-800/40 rounded-xl border border-slate-700/50 p-4">
                  <div className="space-y-4">
                    {/* Toolbar */}
                    <div className="flex gap-2 border-b border-slate-600/50 pb-3">
                      { }
                      {[...Array(8)].map((_, i) => (
                        <SkeletonBase key={`toolbar-${i}`} className="w-8 h-8" />
                      ))}
                      { }
                    </div>
                    {/* Content area */}
                    <div className="space-y-3">
                      { }
                      {[...Array(12)].map((_, i) => (
                        <SkeletonBase
                          key={`content-${i}`}
                          className={`h-4 ${
                            i % 4 === 3
                              ? "w-3/4"
                              : i % 3 === 1
                              ? "w-5/6"
                              : "w-full"
                          }`}
                        />
                      ))}
                      { }
                    </div>
                  </div>
                </div>
              </div>
              {/* Excerpt */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-16" />
                <SkeletonBase className="w-full h-24 rounded-md" />
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-lg">
            <div className="px-4 py-3 border-b border-slate-600/50">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-slate-700/50 rounded animate-pulse"></div>
                <h3 className="text-sm font-semibold text-white">
                  FAQ Section
                </h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Add FAQ Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <SkeletonBase className="h-4 w-16" />
                  <SkeletonBase className="w-full h-12 rounded-md" />
                </div>
                <div className="space-y-2">
                  <SkeletonBase className="h-4 w-14" />
                  <SkeletonBase className="w-full h-24 rounded-md" />
                </div>
                <SkeletonBase className="h-10 w-24 rounded-md" />
              </div>
              {/* Existing FAQs */}
              <div className="space-y-3">
                { }
                {[...Array(2)].map((_, i) => (
                  <div
                    key={`faq-${i}`}
                    className="p-4 bg-slate-700/20 rounded-lg border border-slate-600/30"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <SkeletonBase className="h-5 w-3/4" />
                      <SkeletonBase className="h-8 w-8 rounded" />
                    </div>
                    <div className="space-y-2">
                      <SkeletonBase className="h-4 w-full" />
                      <SkeletonBase className="h-4 w-5/6" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SEO Section */}
          <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-lg">
            <div className="px-4 py-3 border-b border-slate-600/50">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-slate-700/50 rounded animate-pulse"></div>
                <h3 className="text-sm font-semibold text-white">
                  Search Engine Listing
                </h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Meta Title */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-20" />
                <SkeletonBase className="w-full h-12 rounded-md" />
              </div>
              {/* Meta Description */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-28" />
                <SkeletonBase className="w-full h-24 rounded-md" />
              </div>
              {/* Canonical URL */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-24" />
                <SkeletonBase className="w-full h-12 rounded-md" />
              </div>
              {/* Social Media Images */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <SkeletonBase className="h-4 w-20" />
                  <SkeletonBase className="w-full h-12 rounded-md" />
                </div>
                <div className="space-y-2">
                  <SkeletonBase className="h-4 w-20" />
                  <SkeletonBase className="w-full h-12 rounded-md" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Column - Sidebar (1/3) */}
        <div className="lg:col-span-1 space-y-6">
          {/* Visibility & Schedule */}
          <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-lg">
            <div className="px-4 py-3 border-b border-slate-600/50">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-slate-700/50 rounded animate-pulse"></div>
                <h3 className="text-sm font-semibold text-white">Visibility</h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Visibility Radio */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <SkeletonBase className="w-4 h-4 rounded-full" />
                  <SkeletonBase className="h-4 w-16" />
                </div>
                <div className="flex items-center space-x-2">
                  <SkeletonBase className="w-4 h-4 rounded-full" />
                  <SkeletonBase className="h-4 w-12" />
                </div>
              </div>
              {/* Schedule Options */}
              <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600/50 space-y-3">
                <div className="flex items-center space-x-2">
                  <SkeletonBase className="w-4 h-4 rounded-full" />
                  <SkeletonBase className="h-4 w-20" />
                </div>
                <div className="flex items-center space-x-2">
                  <SkeletonBase className="w-4 h-4 rounded-full" />
                  <SkeletonBase className="h-4 w-24" />
                </div>
              </div>
              {/* DateTime picker */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-20" />
                <SkeletonBase className="h-10 w-full rounded-md" />
              </div>
            </div>
          </div>

          {/* Featured Image Section */}
          <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-lg">
            <div className="px-4 py-3 border-b border-slate-600/50">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-slate-700/50 rounded animate-pulse"></div>
                <h3 className="text-sm font-semibold text-white">
                  Featured Image
                </h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Upload mode buttons */}
              <div className="flex gap-2">
                <SkeletonBase className="h-9 w-16 rounded-md" />
                <SkeletonBase className="h-9 w-16 rounded-md" />
              </div>
              {/* URL input */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-20" />
                <SkeletonBase className="w-full h-12 rounded-md" />
              </div>
              {/* Alt text input */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-16" />
                <SkeletonBase className="w-full h-12 rounded-md" />
              </div>
            </div>
          </div>

          {/* Organization Section */}
          <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-lg">
            <div className="px-4 py-3 border-b border-slate-600/50">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-slate-700/50 rounded animate-pulse"></div>
                <h3 className="text-sm font-semibold text-white">
                  Organization
                </h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Author Select */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-12" />
                <SkeletonBase className="w-full h-10 rounded-md" />
              </div>
              {/* Tags Section */}
              <div className="space-y-3">
                <SkeletonBase className="h-4 w-12" />
                <div className="flex flex-wrap gap-1.5">
                  { }
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={`tag-${i}`}
                      className="flex items-center bg-slate-700/30 rounded-md px-2 py-1 gap-1"
                    >
                      <SkeletonBase
                        className={`h-4 ${
                          i === 0 ? "w-12" : i === 1 ? "w-16" : "w-8"
                        }`}
                      />
                      <SkeletonBase className="w-4 h-4 rounded" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <SkeletonBase className="flex-1 h-10 rounded-md" />
                  <SkeletonBase className="w-10 h-10 rounded-md" />
                </div>
              </div>
            </div>
          </div>

          {/* Language Section */}
          <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-lg">
            <div className="px-4 py-3 border-b border-slate-600/50">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-slate-700/50 rounded animate-pulse"></div>
                <h3 className="text-sm font-semibold text-white">Language</h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Language Select */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-16" />
                <SkeletonBase className="w-full h-10 rounded-md" />
              </div>
              {/* Available Translations */}
              <div className="space-y-2">
                <SkeletonBase className="h-4 w-32" />
                <div className="flex gap-2">
                  <SkeletonBase className="h-8 w-12 rounded-md" />
                  <SkeletonBase className="h-8 w-12 rounded-md" />
                  <SkeletonBase className="h-8 w-12 rounded-md" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default SkeletonBase;
